import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Star, ArrowUpRight } from "lucide-react";
import { SITE } from "../data/site";

/** Curated excerpts from Design Desire LLP's Google Business profile (5.0 / 10 reviews). */
const REVIEWS = [
    {
        quote: "Top-notch quality, excellent design sense, and impeccable execution.",
        name: "Sourabh Kulkarni",
        meta: "Google Review · 5★",
    },
    {
        quote: "Very nice design. The durability and workmanship of the interior is very good.",
        name: "Vijay More",
        meta: "Google Review · 5★",
    },
    {
        quote: "Modern and space-saving work. Highly recommended for anyone looking to redesign their home.",
        name: "Ranjana Jasabhati",
        meta: "Google Review · 5★",
    },
];

export const Testimonials = () => {
    const [i, setI] = useState(0);
    const t = REVIEWS[i];

    const prev = () => setI((v) => (v - 1 + REVIEWS.length) % REVIEWS.length);
    const next = () => setI((v) => (v + 1) % REVIEWS.length);

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="py-24 lg:py-36"
        >
            <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid grid-cols-12 gap-6 lg:gap-12 items-start">
                    <div className="col-span-12 lg:col-span-3">
                        <div className="overline text-[#4A4643] mb-6">
                            Client reviews
                        </div>
                        <h2 className="font-serif text-3xl lg:text-4xl font-light tracking-tight text-[#1A1A1A]">
                            Rated 5.0 on Google.
                        </h2>

                        <div
                            className="mt-6 inline-flex items-center gap-3 border border-[#1A1A1A]/15 px-4 py-3"
                            data-testid="google-rating-badge"
                        >
                            <div className="flex gap-0.5">
                                {Array.from({ length: 5 }).map((_, s) => (
                                    <Star
                                        key={s}
                                        size={14}
                                        strokeWidth={1.5}
                                        className="fill-[#F5A623] text-[#F5A623]"
                                    />
                                ))}
                            </div>
                            <div className="text-sm">
                                <span className="font-medium text-[#1A1A1A]">5.0</span>{" "}
                                <span className="text-[#4A4643]">· 10 reviews</span>
                            </div>
                        </div>

                        <a
                            href={SITE.googleReviewsUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="google-reviews-cta"
                            className="mt-8 inline-flex items-center gap-2 overline text-[#1A1A1A] dd-link"
                        >
                            Read all on Google
                            <ArrowUpRight size={14} strokeWidth={1.6} />
                        </a>
                    </div>

                    <div className="col-span-12 lg:col-span-8 lg:col-start-5 relative min-h-[260px]">
                        <AnimatePresence mode="wait">
                            <motion.blockquote
                                key={i}
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -14 }}
                                transition={{ duration: 0.5 }}
                                data-testid={`testimonial-${i}`}
                            >
                                <p className="font-serif text-2xl md:text-3xl lg:text-[40px] leading-[1.25] text-[#1A1A1A] font-light tracking-tight">
                                    &ldquo;{t.quote}&rdquo;
                                </p>
                                <footer className="mt-8 flex items-center gap-4 flex-wrap">
                                    <span className="font-serif text-lg text-[#1A1A1A]">
                                        {t.name}
                                    </span>
                                    <span className="overline text-[#8C7C6D]">
                                        {t.meta}
                                    </span>
                                </footer>
                            </motion.blockquote>
                        </AnimatePresence>

                        <div className="mt-12 flex items-center gap-3">
                            <button
                                onClick={prev}
                                data-testid="testimonial-prev"
                                className="w-12 h-12 border border-[#1A1A1A]/30 hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-all flex items-center justify-center"
                                aria-label="Previous testimonial"
                            >
                                <ChevronLeft size={18} strokeWidth={1.5} />
                            </button>
                            <button
                                onClick={next}
                                data-testid="testimonial-next"
                                className="w-12 h-12 border border-[#1A1A1A]/30 hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-all flex items-center justify-center"
                                aria-label="Next testimonial"
                            >
                                <ChevronRight size={18} strokeWidth={1.5} />
                            </button>
                            <div className="ml-4 overline text-[#4A4643]">
                                {String(i + 1).padStart(2, "0")} /{" "}
                                {String(REVIEWS.length).padStart(2, "0")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
